import { mui } from "@/mui";
import { useMobyDB, useSession } from "./provider";
import { useState, useEffect, useCallback, type ReactNode } from "react";
import { XIcon, CalendarIcon, CalendarCheck, CalendarPlus, Settings2, CheckIcon, XCircleIcon, HelpCircleIcon, ClockIcon, MapPinIcon } from "lucide-react";
import {
  useUpcomingEvents,
  usePendingInvites,
  useCalendarCRUD,
  getCalendarSettings,
  setCalendarSettings,
} from "@/hooks/useCalendarEvents";
import { CalendarRSVP, type CalendarEvent, type CalendarInvite } from "@/lib/calendar-types";

// ─── Helper: relative time label ────────────────────────────────────────────

function relativeTimeLabel(ms: number): string {
  const now = Date.now();
  const diff = ms - now;
  if (diff < 0) return "past";
  if (diff < 60_000) return "now";
  if (diff < 3_600_000) return `in ${Math.ceil(diff / 60_000)}m`;
  if (diff < 86_400_000) return `in ${Math.floor(diff / 3_600_000)}h`;
  const days = Math.floor(diff / 86_400_000);
  return days === 1 ? "tomorrow" : `in ${days}d`;
}

function formatTime(ms: number): string {
  return new Date(ms).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

function formatDate(ms: number): string {
  return new Date(ms).toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" });
}

// ─── Invite Card ────────────────────────────────────────────────────────────

function InviteCard({
  invite,
  onRespond,
}: {
  invite: CalendarInvite;
  onRespond: (inviteId: string, eventId: string, rsvp: CalendarRSVP) => void;
}) {
  const snapshot = invite.eventSnapshot;
  const isPending = invite.rsvp === CalendarRSVP.PENDING;
  const isExpired = snapshot?.endTime ? snapshot.endTime < Date.now() : false;

  return (
    <div className={`bg-surface-container-highest rounded-lg p-4 space-y-2 ${isExpired ? "opacity-70" : ""}`}>
      <div className="flex items-start gap-3">
        <div className={`grid size-10 shrink-0 place-items-center rounded-full ${isExpired ? "bg-outline text-on-surface-variant" : "bg-primary text-on-primary"}`}>
          <CalendarCheck size={20} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-body-lg font-medium truncate">{snapshot?.title || "Untitled event"}</div>
          <div className="text-body-sm text-on-surface-variant flex items-center gap-1">
            <ClockIcon size={12} />
            {snapshot?.startTime
              ? `${formatDate(snapshot.startTime)} · ${formatTime(snapshot.startTime)}`
              : "No time set"}
          </div>
          {snapshot?.allDay && (
            <div className="text-body-sm text-on-surface-variant">All day</div>
          )}
          {isExpired && (
            <div className="text-label-sm text-error mt-1 font-medium">
              This event has already passed
            </div>
          )}
          <div className="text-label-sm text-on-surface-variant mt-1">
            From: {invite.senderAddress.slice(0, 12)}…
          </div>
          {snapshot?.groupName && (
            <div className="text-label-sm text-tertiary mt-1 flex items-center gap-1">
              <span className="inline-block size-2 rounded-full bg-tertiary" />
              Group: {snapshot.groupName}
            </div>
          )}
        </div>
      </div>

      {isPending ? (
        isExpired ? (
          <div className="text-label-sm text-on-surface-variant pt-1 italic">
            RSVP is no longer available — event has ended
          </div>
        ) : (
          <>
            <div className="flex gap-2 pt-1">
              <button
                onClick={() => onRespond(invite.id, invite.eventId, CalendarRSVP.ACCEPTED)}
                className="flex-1 bg-tertiary text-on-tertiary hover:bg-tertiary/80 rounded-full py-1.5 px-3 flex items-center justify-center gap-1 text-label-md transition-colors"
              >
                <CheckIcon size={14} /> Accept
              </button>
              <button
                onClick={() => onRespond(invite.id, invite.eventId, CalendarRSVP.DECLINED)}
                className="flex-1 bg-error text-on-error hover:bg-error/80 rounded-full py-1.5 px-3 flex items-center justify-center gap-1 text-label-md transition-colors"
              >
                <XCircleIcon size={14} /> Decline
              </button>
              <button
                onClick={() => onRespond(invite.id, invite.eventId, CalendarRSVP.TENTATIVE)}
                className="bg-surface-container hover:bg-surface-container-high border border-outline-variant rounded-full py-1.5 px-3 flex items-center justify-center gap-1 text-label-md transition-colors"
              >
                <HelpCircleIcon size={14} /> Maybe
              </button>
            </div>
            {snapshot?.groupName && (
              <p className="text-label-sm text-on-surface-variant/70 mt-1.5 italic">
                Declining will also leave the &ldquo;{snapshot.groupName}&rdquo; group chat.
              </p>
            )}
          </>
        )
      ) : (
        <div className="text-label-sm text-on-surface-variant pt-1">
          Responded: <span className="font-medium capitalize">{invite.rsvp}</span>
        </div>
      )}
    </div>
  );
}

// ─── Event Card ─────────────────────────────────────────────────────────────

function EventCard({ event }: { event: CalendarEvent }) {
  const timeLabel = relativeTimeLabel(event.startTime);
  const isPast = event.startTime < Date.now();

  return (
    <div className={`bg-surface-container-highest rounded-lg p-3 flex items-start gap-3 ${isPast ? "opacity-60" : ""}`}>
      <div
        className="mt-0.5 size-3 shrink-0 rounded-full"
        style={{ backgroundColor: event.color || "#6750A4" }}
      />
      <div className="flex-1 min-w-0">
        <div className="text-body-md font-medium truncate">{event.title}</div>
        <div className="text-body-sm text-on-surface-variant">
          {event.allDay ? "All day" : `${formatTime(event.startTime)} – ${formatTime(event.endTime)}`}
          {" · "}
          {formatDate(event.startTime)}
        </div>
        {event.location && (
          <div className="text-body-sm text-on-surface-variant flex items-center gap-1 mt-0.5">
            <MapPinIcon size={12} /> {event.location}
          </div>
        )}
      </div>
      <span className="text-label-sm text-on-surface-variant shrink-0 tabular-nums">{timeLabel}</span>
    </div>
  );
}

// ─── Settings Panel ─────────────────────────────────────────────────────────

function CalendarSettingsPanel() {
  const [settings, setSettings] = useState(getCalendarSettings);

  const update = (patch: Partial<typeof settings>) => {
    const next = { ...settings, ...patch };
    setSettings(next);
    setCalendarSettings(next);
  };

  return (
    <div className="space-y-4">
      <h3 className="text-title-sm text-primary">Calendar Settings</h3>

      {/* Timezone */}
      <div>
        <label className="text-label-md text-on-surface block mb-1">Timezone</label>
        <input
          type="text"
          value={settings.timezone}
          onChange={e => update({ timezone: e.target.value })}
          className="w-full rounded-small bg-surface-container px-3 py-2 text-body-md text-on-surface border border-outline-variant focus:border-primary outline-none"
          placeholder="e.g. America/Los_Angeles"
        />
      </div>

      {/* Working hours */}
      <div className="flex gap-3">
        <div className="flex-1">
          <label className="text-label-md text-on-surface block mb-1">Work start</label>
          <input
            type="number"
            min={0}
            max={23}
            title="Work start hour"
            value={settings.workingHoursStart}
            onChange={e => update({ workingHoursStart: Number(e.target.value) })}
            className="w-full rounded-small bg-surface-container px-3 py-2 text-body-md text-on-surface border border-outline-variant focus:border-primary outline-none"
          />
        </div>
        <div className="flex-1">
          <label className="text-label-md text-on-surface block mb-1">Work end</label>
          <input
            type="number"
            min={0}
            max={23}
            title="Work end hour"
            value={settings.workingHoursEnd}
            onChange={e => update({ workingHoursEnd: Number(e.target.value) })}
            className="w-full rounded-small bg-surface-container px-3 py-2 text-body-md text-on-surface border border-outline-variant focus:border-primary outline-none"
          />
        </div>
      </div>

      {/* Default reminder */}
      <div>
        <label className="text-label-md text-on-surface block mb-1">Default reminder (minutes)</label>
        <input
          type="number"
          min={0}
          title="Default reminder minutes before event"
          value={settings.defaultReminders?.[0]?.minutesBefore ?? 15}
          onChange={e => {
            const mins = Number(e.target.value);
            update({ defaultReminders: [{ minutesBefore: mins, method: 'notification' as any }] });
          }}
          className="w-full rounded-small bg-surface-container px-3 py-2 text-body-md text-on-surface border border-outline-variant focus:border-primary outline-none"
        />
      </div>

      {/* Default visibility */}
      <div>
        <label className="text-label-md text-on-surface block mb-1">Default visibility</label>
        <select
          value={settings.defaultVisibility}
          title="Default event visibility"
          onChange={e => update({ defaultVisibility: e.target.value as any })}
          className="w-full rounded-small bg-surface-container px-3 py-2 text-body-md text-on-surface border border-outline-variant focus:border-primary outline-none"
        >
          <option value="busy">Busy</option>
          <option value="free">Free</option>
          <option value="tentative">Tentative</option>
        </select>
      </div>

      {/* Publish availability toggle */}
      <label className="flex items-center gap-3 cursor-pointer">
        <input
          type="checkbox"
          checked={settings.publishAvailability}
          onChange={e => update({ publishAvailability: e.target.checked })}
          className="accent-primary size-4"
        />
        <span className="text-body-md text-on-surface">Publish availability to contacts</span>
      </label>
    </div>
  );
}

// ─── CalendarDrawer ─────────────────────────────────────────────────────────

export function CalendarDrawer({
  children,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
}: {
  children: ReactNode | ((props: { unseenCount: number }) => ReactNode);
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  const session = useSession();
  const db = useMobyDB();
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const isOpen = controlledOpen !== undefined ? controlledOpen : uncontrolledOpen;
  const setIsOpen = (open: boolean) => {
    setUncontrolledOpen(open);
    controlledOnOpenChange?.(open);
  };
  const [activeTab, setActiveTab] = useState<string>("invites");

  // Data hooks — must be called unconditionally (React rules of hooks)
  const pendingInvites = usePendingInvites();
  const upcomingEvents = useUpcomingEvents(10);
  const { respondToInvite } = useCalendarCRUD();

  // Listen for new invite events to flash the drawer
  const [inviteFlash, setInviteFlash] = useState(false);
  useEffect(() => {
    const handler = () => {
      setInviteFlash(true);
      setTimeout(() => setInviteFlash(false), 3000);
    };
    window.addEventListener("calendar-invite-received", handler);
    return () => window.removeEventListener("calendar-invite-received", handler);
  }, []);

  // Listen for external open requests
  useEffect(() => {
    const open = () => setIsOpen(true);
    window.addEventListener("open-calendar-drawer", open);
    return () => window.removeEventListener("open-calendar-drawer", open);
  }, []);

  const handleRespond = useCallback(async (inviteId: string, eventId: string, rsvp: CalendarRSVP) => {
    try {
      await respondToInvite(inviteId, rsvp);
      // Notify provider layer so it can send the P2P RSVP back to the organizer
      window.dispatchEvent(new CustomEvent('calendar-invite-rsvp-sent', {
        detail: { inviteId, eventId, rsvp },
      }));
    } catch (err) {
      console.error("[CALENDAR_DRAWER] RSVP failed:", err);
    }
  }, [respondToInvite]);

  const unseenCount = pendingInvites.length;

  if (session === undefined) return null;

  return (
    <mui.Drawer.Root open={isOpen} onOpenChange={setIsOpen} direction="left">
      {controlledOpen === undefined ? (
        <mui.Drawer.Trigger asChild>
          {typeof children === "function" ? children({ unseenCount }) : children}
        </mui.Drawer.Trigger>
      ) : (
        typeof children === "function" ? children({ unseenCount }) : children
      )}
      <mui.Drawer.Portal>
        <mui.Drawer.Overlay className="sm:bg-transparent" />
        <mui.Drawer.Content className="bg-surface-container-high text-on-surface fixed inset-y-0 left-0 z-50 flex h-full w-full flex-col border-r border-outline sm:ml-20 sm:w-80 overflow-hidden">
          {/* Pointer arrow */}
          <div className="hidden sm:block absolute -left-2 top-6 w-0 h-0 border-y-[8px] border-y-transparent border-r-[8px] border-r-surface-container-high" />
          <div className="border-b-outline flex items-center justify-between border-b p-4">
            <mui.Drawer.Title className="text-headline-sm">MobyCalendar™</mui.Drawer.Title>
            <button
              onClick={() => setIsOpen(false)}
              className="sm:hidden p-1 rounded-full hover:bg-on-surface-variant/8"
              aria-label="Close drawer"
            >
              <XIcon size={20} />
            </button>
          </div>

          <mui.Tabs.Root
            value={activeTab}
            onValueChange={setActiveTab}
            className="flex min-w-0 flex-1 flex-col overflow-hidden"
          >
            <mui.Tabs.List className="shrink-0">
              <mui.Tabs.Tab value="invites">
                <div className="relative">
                  Invites
                  {pendingInvites.length > 0 && (
                    <span className="absolute -top-1 -right-3 bg-primary text-on-primary text-[10px] rounded-full min-w-[16px] h-[16px] flex items-center justify-center px-1">
                      {pendingInvites.length}
                    </span>
                  )}
                </div>
              </mui.Tabs.Tab>
              <mui.Tabs.Tab value="schedule">Schedule</mui.Tabs.Tab>
              <mui.Tabs.Tab value="settings">Settings</mui.Tabs.Tab>
            </mui.Tabs.List>

            {/* ── Invites Tab ── */}
            <mui.Tabs.Panel value="invites" className="flex-1 overflow-y-auto p-4">
              {pendingInvites.length === 0 ? (
                <div className="text-center py-8">
                  <CalendarCheck size={40} className="mx-auto text-on-surface-variant/40 mb-3" />
                  <p className="text-body-md text-on-surface-variant">No pending invites</p>
                  <p className="text-body-sm text-on-surface-variant/70 mt-1">
                    Invitations from contacts will appear here.
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {pendingInvites.map(invite => (
                    <InviteCard
                      key={invite.id}
                      invite={invite}
                      onRespond={handleRespond}
                    />
                  ))}
                </div>
              )}
            </mui.Tabs.Panel>

            {/* ── Schedule Tab ── */}
            <mui.Tabs.Panel value="schedule" className="flex-1 overflow-y-auto p-4">
              <button
                onClick={() => {
                  window.dispatchEvent(new CustomEvent('calendar-panel-open'));
                  setIsOpen(false);
                }}
                className="w-full mb-4 flex items-center justify-center gap-2 rounded-full bg-primary text-on-primary py-2.5 px-4 text-label-lg hover:bg-primary/90 transition-colors"
              >
                <CalendarIcon size={18} />
                Open Calendar
              </button>
              <h3 className="text-title-sm text-primary mb-3">Upcoming</h3>
              {upcomingEvents.length === 0 ? (
                <div className="text-center py-8">
                  <CalendarPlus size={40} className="mx-auto text-on-surface-variant/40 mb-3" />
                  <p className="text-body-md text-on-surface-variant">No upcoming events</p>
                  <p className="text-body-sm text-on-surface-variant/70 mt-1">
                    Events you create or accept will appear here.
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {upcomingEvents.map(event => (
                    <EventCard key={event.id} event={event} />
                  ))}
                </div>
              )}
            </mui.Tabs.Panel>

            {/* ── Settings Tab ── */}
            <mui.Tabs.Panel value="settings" className="flex-1 overflow-y-auto p-4">
              <CalendarSettingsPanel />
            </mui.Tabs.Panel>
          </mui.Tabs.Root>
        </mui.Drawer.Content>
      </mui.Drawer.Portal>
    </mui.Drawer.Root>
  );
}
